import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { addTracker, getIstoricTracker, getStreak, getInsights } from '../services/api';

const Tracker: React.FC = () => {
  const [nivel, setNivel] = useState(5);
  const [zona, setZona] = useState('Lombara (partea de jos a spatelui)');
  const [observatii, setObservatii] = useState('');
  const [exercitiiFacute, setExercitiiFacute] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mesaj, setMesaj] = useState('');
  const [streak, setStreak] = useState(0);
  const [completatAzi, setCompletatAzi] = useState(false);
  const [istoric, setIstoricList] = useState<any[]>([]);
  const [filtru, setFiltru] = useState('Saptamana');
  const [insights, setInsights] = useState<any>(null);

  const loadDate = useCallback(async () => {
    try {
      const [streakRes, istoricRes] = await Promise.all([
        getStreak(),
        getIstoricTracker(filtru)
      ]);
      setStreak(streakRes.data.streak);
      setCompletatAzi(streakRes.data.completat_azi);
      setIstoricList(istoricRes.data);
    } catch {}
    try {
      const insightsRes = await getInsights();
      setInsights(insightsRes.data);
    } catch {}
  }, [filtru]);

  useEffect(() => {
    loadDate();
  }, [loadDate]);

  const handleSalveaza = async () => {
    setLoading(true);
    try {
      await addTracker(nivel, zona, observatii || undefined, exercitiiFacute || undefined);
      setMesaj('Înregistrat cu succes!');
      setObservatii('');
      setExercitiiFacute(false);
      loadDate();
      setTimeout(() => setMesaj(''), 3000);
    } catch {
      setMesaj('Eroare la salvare!');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '0.75rem', borderRadius: '10px',
    border: '1.5px solid #2a2a4a', background: '#1a1a30',
    color: '#e0e0f0', fontSize: '1rem', width: '100%',
    boxSizing: 'border-box' as const
  };

  const nivelColor = nivel <= 3 ? '#60d090' : nivel <= 6 ? '#d0a040' : '#d06060';

  const chartData = istoric.map(r => ({
    data: r.ora?.substring(0, 10) + ' ' + (r.ora?.substring(11, 16) || ''),
    durere: r.nivel_durere
  }));

  return (
    <div style={{ padding: '2rem', maxWidth: '700px', margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}>
      <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>📊 Tracker Durere</h2>
      <p style={{ color: '#8080a0', marginBottom: '1.5rem' }}>Monitorizează evoluția durerii tale zilnic.</p>

      {/* Streak */}
      {streak > 0 && (
        <div style={{
          background: streak >= 7 ? '#0f2a1a' : streak >= 3 ? '#2a1a00' : '#1a1a30',
          border: `1px solid ${streak >= 7 ? '#2a6a4a' : streak >= 3 ? '#6a4a00' : '#2a2a4a'}`,
          borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem'
        }}>
          <p style={{ color: streak >= 7 ? '#60d090' : streak >= 3 ? '#d0a040' : '#a0a0d0', margin: 0 }}>
            {streak >= 7 ? `⚡ Seria ta: ${streak} zile consecutive!` :
             streak >= 3 ? `🔥 Seria ta: ${streak} zile! Nu o pierde!` :
             `📅 Seria ta: ${streak} zile`}
          </p>
        </div>
      )}

      {/* Banner informativ dacă a înregistrat deja azi */}
      {completatAzi && (
        <div style={{ background: '#1a2a3a', border: '1px solid #2a4a6a', borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <p style={{ color: '#60a0d0', margin: 0, fontSize: '0.9rem' }}>
            💡 Ai înregistrat deja azi — poți adăuga oricâte înregistrări dorești.
          </p>
        </div>
      )}

      {/* Form */}
      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid #2a2a4a' }}>
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Cum te simți acum?</h3>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <p style={{ color: '#c0c0e0', margin: 0 }}>Nivelul durerii:</p>
              <strong style={{ color: nivelColor, fontSize: '1.2rem' }}>{nivel}/10</strong>
            </div>
            <input type="range" min={1} max={10} value={nivel}
              onChange={e => setNivel(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: nivelColor }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
              <div>
                <span style={{ color: '#6060a0', fontSize: '0.75rem', display: 'block' }}>1</span>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Fără durere</small>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#6060a0', fontSize: '0.75rem', display: 'block' }}>10</span>
                <small style={{ color: '#6060a0', fontSize: '0.72rem' }}>Nu mă pot mișca/nu pot merge</small>
              </div>
            </div>
          </div>
          <select value={zona} onChange={e => setZona(e.target.value)} style={{ ...inputStyle, marginBottom: '1rem' }}>
            {['Lombara (partea de jos a spatelui)', 'Sciatica (fesa + picior)', 'Cervicala (gat)', 'Toracica (mijlocul spatelui)', 'Multiple zone'].map(v => <option key={v}>{v}</option>)}
          </select>
          <textarea placeholder="Observații (opțional)..." value={observatii}
            onChange={e => setObservatii(e.target.value)}
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', marginBottom: '1rem' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
            <input type="checkbox" checked={exercitiiFacute}
              onChange={e => setExercitiiFacute(e.target.checked)}
              style={{ accentColor: '#4a4ad8', width: '16px', height: '16px' }} />
            <span style={{ color: '#a0a0c0', fontSize: '0.9rem' }}>Am urmat exercitiile din plan azi</span>
          </label>
          {mesaj && <p style={{ color: mesaj.includes('succes') ? '#60d090' : '#d06060', marginBottom: '1rem' }}>{mesaj}</p>}
          <button onClick={handleSalveaza} disabled={loading} style={{
            width: '100%', padding: '0.75rem', borderRadius: '10px', border: 'none',
            background: '#4a4ad8', color: '#fff', fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1
          }}>{loading ? 'Se salvează...' : 'Salvează înregistrarea'}</button>
        </div>

      {/* Insights */}
      {insights && insights.nr_inregistrari_7_zile >= 3 && (
        <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1rem' }}>Analiza ultimelor 7 zile</h3>
          {insights.trend_directie === 'imbunatatire' && (
            <p style={{ color: '#60d090', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Durerea a scazut cu {insights.trend_procent}% fata de saptamana anterioara
            </p>
          )}
          {insights.trend_directie === 'agravare' && (
            <p style={{ color: '#d06060', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Durerea a crescut cu {insights.trend_procent}% fata de saptamana anterioara
            </p>
          )}
          {insights.trend_directie === 'stabil' && (
            <p style={{ color: '#a0a0c0', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Durerea este stabila fata de saptamana anterioara
            </p>
          )}
          {insights.zi_dificila && (
            <p style={{ color: '#d0a040', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Ziua cea mai dificila: {insights.zi_dificila}
            </p>
          )}
          {insights.zona_frecventa && (
            <p style={{ color: '#a0a0c0', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Zona cel mai des afectata la durere mare: {insights.zona_frecventa}
            </p>
          )}
          {insights.zile_exercitii_7_zile > 0 && (
            <p style={{ color: '#60d090', margin: 0, fontSize: '0.9rem' }}>
              Exercitii urmate in {insights.zile_exercitii_7_zile} din ultimele 7 zile
            </p>
          )}
        </div>
      )}

      {/* Istoric */}
      <div style={{ background: '#1a1a30', borderRadius: '16px', padding: '1.5rem', border: '1px solid #2a2a4a' }}>
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Istoricul tău</h3>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {['Azi', 'Saptamana', 'Luna', 'An', 'Tot'].map(f => (
            <button key={f} onClick={() => setFiltru(f)} style={{
              padding: '0.4rem 0.8rem', borderRadius: '8px', border: 'none',
              background: filtru === f ? '#4a4ad8' : '#2a2a4a',
              color: '#fff', cursor: 'pointer', fontSize: '0.85rem'
            }}>{f}</button>
          ))}
        </div>

        {istoric.length === 0 ? (
          <p style={{ color: '#6060a0' }}>Nu ai înregistrări pentru perioada selectată.</p>
        ) : (
          <>
            {/* Grafic */}
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#a0a0c0', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Evoluția durerii — {filtru}</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                  <XAxis dataKey="data" stroke="#4a4a6a" tick={{ fill: '#6060a0', fontSize: 11 }} />
                  <YAxis domain={[0, 10]} stroke="#4a4a6a" tick={{ fill: '#6060a0', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a30', border: '1px solid #2a2a4a', borderRadius: '8px', color: '#e0e0f0' }}
                    labelStyle={{ color: '#a0a0c0' }}
                  />
                  <Line type="monotone" dataKey="durere" stroke="#4a4ad8" strokeWidth={2} dot={{ fill: '#4a4ad8', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {[
                { label: 'Medie', value: `${(istoric.reduce((s, r) => s + r.nivel_durere, 0) / istoric.length).toFixed(1)}/10`, color: '#fff' },
                { label: 'Cel mai bine', value: `${Math.min(...istoric.map(r => r.nivel_durere))}/10`, color: '#60d090' },
                { label: 'Înregistrări', value: istoric.length, color: '#fff' }
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, background: '#12121f', borderRadius: '10px', padding: '0.75rem', textAlign: 'center' }}>
                  <p style={{ color: '#6060a0', fontSize: '0.7rem', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>{s.label}</p>
                  <p style={{ color: s.color, fontWeight: 600, margin: 0 }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Lista */}
            {[...istoric].reverse().map((r, i) => {
              const c = r.nivel_durere <= 3 ? '#60d090' : r.nivel_durere <= 6 ? '#d0a040' : '#d06060';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '0.75rem', borderRadius: '8px', background: '#12121f', marginBottom: '0.5rem'
                }}>
                  <span style={{ color: '#6060a0', fontSize: '0.8rem', minWidth: '120px' }}>
                    {r.data} {r.ora?.substring(11, 16)}
                  </span>
                  <span style={{ color: c, fontWeight: 600, minWidth: '50px' }}>{r.nivel_durere}/10</span>
                  <span style={{ color: '#a0a0c0', fontSize: '0.85rem', flex: 1 }}>{r.zona}</span>
                  {r.exercitii_facute && (
                    <span style={{ color: '#60d090', fontSize: '0.78rem' }}>Exercitii urmate</span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default Tracker;
